import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Card,
  CardContent,
  Chip,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  LineChart,
  Line,
  Area,
  AreaChart
} from 'recharts';
import { useTranslation } from 'react-i18next';
import { UserActivityReport } from '../../types/reports';

interface UserActivityChartProps {
  data: UserActivityReport;
}

const UserActivityChart: React.FC<UserActivityChartProps> = ({ data }) => {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8'];

  const getDateLocale = () => {
    const lang = i18n.language;
    if (lang === 'pt-BR') return 'pt-BR';
    if (lang === 'es-ES') return 'es-ES';
    return 'en-US';
  };

  const dailyData = data.dailyActivity.map(stat => ({
    ...stat,
    date: new Date(stat.date).toLocaleDateString(getDateLocale(), { 
      day: '2-digit', 
      month: '2-digit' 
    })
  }));

  const userData = data.userStats.map(stat => ({
    ...stat,
    userName: stat.userName.length > 15 
      ? stat.userName.substring(0, 15) + '...' 
      : stat.userName
  }));

  const inactiveUsers = data.totalUsers - data.activeUsers;
  const statusData = [
    { name: t('pages.reports.charts.userActivity.active'), value: data.activeUsers, color: theme.palette.success.main },
    { name: t('pages.reports.charts.userActivity.inactive'), value: inactiveUsers, color: theme.palette.error.main }
  ];

  const roleData = data.roleBreakdown.map(role => ({
    ...role,
    name: role.role
  }));

  return (
    <Box sx={{ 
      width: '100%', 
      maxWidth: '100%', 
      overflow: 'hidden',
      px: isMobile ? 0 : 0
    }}>
      {/* Summary Cards */}
      <Box sx={{ 
        display: 'flex', 
        flexWrap: 'wrap', 
        gap: isMobile ? 1 : 3, 
        mb: isMobile ? 2 : 4,
        justifyContent: isMobile ? 'center' : 'flex-start'
      }}>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.userActivity.totalUsers')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                {data.totalUsers}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.userActivity.activeUsers')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'success.main' }}>
                {data.activeUsers}
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.userActivity.activityRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                {((data.activeUsers / data.totalUsers) * 100).toFixed(1)}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
        <Box sx={{ 
          flex: isMobile ? '1 1 45%' : '1 1 200px', 
          minWidth: isMobile ? '45%' : '200px',
          maxWidth: isMobile ? '45%' : 'none'
        }}>
          <Card sx={{ height: isMobile ? '80px' : 'auto' }}>
            <CardContent sx={{ p: isMobile ? 1 : 2 }}>
              <Typography color="textSecondary" gutterBottom variant={isMobile ? "caption" : "body2"}>
{t('pages.reports.charts.userActivity.completionRate')}
              </Typography>
              <Typography variant={isMobile ? "h6" : "h4"} sx={{ fontWeight: 'bold', color: 'info.main' }}>
                {data.userStats.length > 0 ? (data.userStats.reduce((sum, user) => sum + user.completionRate, 0) / data.userStats.length).toFixed(1) : 0}%
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Charts */}
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column', 
        gap: isMobile ? 2 : 3,
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden'
      }}>
        {/* Daily Activity Chart */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.dailyActivity')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <AreaChart data={dailyData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="date" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Area
                    type="monotone"
                    dataKey="activeUsers"
                    stackId="1"
                    stroke={theme.palette.primary.main}
                    fill={theme.palette.primary.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.userActivity.activeUsersLabel')}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalLeadsAssigned"
                    stackId="2"
                    stroke={theme.palette.success.main}
                    fill={theme.palette.success.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.userActivity.assignedLeads')}
                  />
                  <Area
                    type="monotone"
                    dataKey="totalConversationsHandled"
                    stackId="3"
                    stroke={theme.palette.warning.main}
                    fill={theme.palette.warning.main}
                    fillOpacity={0.6}
                    name={t('pages.reports.charts.userActivity.handledConversations')}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* User Status Distribution */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.userStatus')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 200 : 300}>
                <PieChart>
                  <Pie
                    data={statusData}
                    cx="50%"
                    cy="50%"
                    labelLine={false}
                    label={({ name, value }) => `${name}: ${value}`}
                    outerRadius={80}
                    fill="#8884d8"
                    dataKey="value"
                  >
                    {statusData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip />
                </PieChart>
              </ResponsiveContainer>
            </Paper>
          </Box>
        </Box>

        {/* User Performance Comparison */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.userPerformance')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <BarChart data={userData} layout="horizontal" margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" />
                  <YAxis dataKey="userName" type="category" width={100} />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="assignedLeads" fill={theme.palette.primary.main} name={t('pages.reports.charts.userActivity.assignedLeadsBar')} />
                  <Bar dataKey="handledConversations" fill={theme.palette.success.main} name={t('pages.reports.charts.userActivity.handledConversationsBar')} />
                  <Bar dataKey="completedConversations" fill={theme.palette.warning.main} name={t('pages.reports.charts.userActivity.completedConversationsBar')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Role Breakdown */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.roleDistribution')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {roleData.map((role, index) => (
                  <Box key={index} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2">{role.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 'bold' }}>
                        {role.count}
                      </Typography>
                      <Chip
                        label={`${role.percentage.toFixed(1)}%`}
                        size="small"
                        color="primary"
                        variant="outlined"
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* Completion Rate Chart */}
        <Box sx={{ 
          display: 'flex', 
          flexDirection: isMobile ? 'column' : 'row',
          flexWrap: isMobile ? 'nowrap' : 'wrap', 
          gap: isMobile ? 2 : 3,
          width: '100%',
          maxWidth: '100%'
        }}>
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 600px', 
            minWidth: isMobile ? '100%' : '600px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.completionRateByUser')}
              </Typography>
              <ResponsiveContainer width="100%" height={isMobile ? 280 : 300}>
                <BarChart data={userData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="userName" />
                  <YAxis />
                  <Tooltip />
                  <Legend />
                  <Bar dataKey="completionRate" fill={theme.palette.info.main} name={t('pages.reports.charts.userActivity.completionRateLabel')} />
                </BarChart>
              </ResponsiveContainer>
            </Paper>
          </Box>

          {/* Activity Summary */}
          <Box sx={{ 
            flex: isMobile ? '1 1 100%' : '1 1 300px', 
            minWidth: isMobile ? '100%' : '300px',
            width: '100%',
            maxWidth: '100%'
          }}>
            <Paper sx={{ 
              p: isMobile ? 1 : 3,
              width: '100%',
              maxWidth: '100%',
              overflow: 'hidden'
            }}>
              <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
                {t('pages.reports.charts.userActivity.activitySummary')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('pages.reports.charts.userActivity.activeUsersToday')}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'success.main' }}>
                    {data.dailyActivity.length > 0 ? data.dailyActivity[data.dailyActivity.length - 1].activeUsers : 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('pages.reports.charts.userActivity.assignedLeadsToday')}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                    {data.dailyActivity.length > 0 ? data.dailyActivity[data.dailyActivity.length - 1].totalLeadsAssigned : 0}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <Typography variant="body2">{t('pages.reports.charts.userActivity.conversationsStartedToday')}</Typography>
                  <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'warning.main' }}>
                    {data.dailyActivity.length > 0 ? data.dailyActivity[data.dailyActivity.length - 1].totalConversationsHandled : 0}
                  </Typography>
                </Box>
              </Box>
            </Paper>
          </Box>
        </Box>

        {/* User Details Table */}
        <Box sx={{ 
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden'
        }}>
          <Paper sx={{ 
            p: isMobile ? 1 : 3,
            width: '100%',
            maxWidth: '100%',
            overflow: 'hidden'
          }}>
            <Typography variant={isMobile ? "subtitle1" : "h6"} gutterBottom sx={{ fontWeight: 'bold' }}>
              {t('pages.reports.charts.userActivity.userDetails')}
            </Typography>
            <Box sx={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid #e0e0e0' }}>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.user')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.status')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.leads')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.conversations')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.completionRateLabel')}</th>
                    <th style={{ padding: '12px', textAlign: 'left' }}>{t('pages.reports.charts.userActivity.role')}</th>
                  </tr>
                </thead>
                <tbody>
                  {userData.map((user, index) => (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '12px' }}>{user.userName}</td>
                      <td style={{ padding: '12px' }}>
                        <Chip
                          label={user.isActive ? t('pages.reports.charts.userActivity.active') : t('pages.reports.charts.userActivity.inactive')}
                          size="small"
                          color={user.isActive ? 'success' : 'error'}
                          variant="outlined"
                        />
                      </td>
                      <td style={{ padding: '12px' }}>{user.assignedLeads}</td>
                      <td style={{ padding: '12px' }}>{user.handledConversations}</td>
                      <td style={{ padding: '12px' }}>{user.completionRate.toFixed(1)}%</td>
                      <td style={{ padding: '12px' }}>{user.role}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default UserActivityChart;